import React from "react";
import ReactSelect from "react-select";
import type { Props as ReactSelectProps } from "react-select";
import styles from "./Select.module.scss";
import withLabelAndError from "../withLabelAndError";
import { Controller } from "react-hook-form";
import { combineClasses } from "@/lib/utils";
import LoadingText from "@/components/LoadingText/LoadingText";

interface SelectOption {
  label: React.ReactNode;
  value: string;
}

interface SelectProps extends ReactSelectProps<SelectOption, boolean> {
  control?: any;
  validations?: any;
  name: string;
  optionsHeight?: number;
  className?: string;
  loadingText?: string;
  isLoading?: boolean;
}

const Select: React.FC<SelectProps> = (props) => {
  const {
    control,
    validations,
    name,
    placeholder,
    isDisabled,
    options,
    optionsHeight,
    isMulti,
    className = "",
    loadingText = "Loading",
    isLoading = false,
    ...rest
  } = props;

  const loadingOption: SelectOption = {
    label: <LoadingText text={loadingText} style={{ fontWeight: "400" }} />,
    value: "loading",
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      background: "rgb(var(--background))",
      border: state.isFocused
        ? "1px solid rgb(var(--primary))"
        : "1px solid rgba(var(--foreground), 0.15)",
      borderRadius: "8px",
      minHeight: "2.75rem",
      height: isMulti ? "maxContent" : "2.75rem",
      boxShadow: state.isFocused
        ? "0 0 0 1px rgb(var(--primary))"
        : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      overflow: isMulti ? "auto !important" : "hidden",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.5 : 1,
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height: "100%",
      padding: "0 0.75rem",
      display: "flex",
      alignItems: "center",
    }),
    input: (provided: any) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: "100%",
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgb(var(--primary))"
        : state.isFocused
          ? "rgba(var(--primary), 0.1)"
          : "transparent",
      color: state.isSelected ? "#ffffff" : "rgb(var(--foreground))",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      "&:active": {
        backgroundColor: state.isSelected
          ? "rgb(var(--primary))"
          : "rgba(var(--primary), 0.15)",
      },
    }),
  };

  const renderInput = (field: any = {}) => (
    <ReactSelect<SelectOption, boolean>
      {...rest}
      {...field}
      id={name}
      className={combineClasses(styles.inputField, className)}
      value={isLoading ? loadingOption : field?.value ?? rest?.value}
      options={isLoading ? [loadingOption] : options}
      styles={customStyles}
      placeholder={placeholder}
      classNamePrefix="react-select"
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
      isDisabled={isDisabled || isLoading}
      isMulti={isMulti}
      // getOptionLabel={(option) =>
      //   typeof option.label === "string" ? option.label : String(option.value)
      // }
      // getOptionValue={(option) => option.value}    getOptionValue={(option) => option.value}
      // 👇 this renders JSX labels correctly
      getOptionValue={(option) => option.value}
      formatOptionLabel={(option) => {
        if (typeof option.label === "string") return option.label;

        if (typeof option.label === "object" && option.label !== null) {
          return option.label || JSON.stringify(option.label);
        }

        return String(option.label);
      }}
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        rules={validations}
        name={name}
        render={({ field }) => renderInput(field)}
      />
    );
  } else {
    return renderInput();
  }
};

const SelectWithLabelAndError = withLabelAndError(Select);

export default SelectWithLabelAndError;
export { Select };
